import { Link } from "react-router-dom";
import CrudList from "../components/CrudList";
import { ArrowRight, Crown, User } from "@phosphor-icons/react";

const tierOptions = [
  { value: "standard", label: "Standard" },
  { value: "silver", label: "Silver" },
  { value: "gold", label: "Gold" },
  { value: "platinum", label: "Platinum VIP" },
];

const FIELDS = [
  { name: "first_name", label: "First name", required: true },
  { name: "last_name", label: "Last name" },
  { name: "email", label: "Email" },
  { name: "phone", label: "Phone" },
  { name: "company", label: "Company" },
  { name: "vip_tier", label: "VIP tier", type: "select", options: tierOptions, default: "standard" },
  { name: "nationality", label: "Nationality" },
  { name: "date_of_birth", label: "Date of birth", type: "date" },
  { name: "age", label: "Age" },
  { name: "address", label: "Address", span: 2 },
  { name: "id_document", label: "ID / Passport" },
  { name: "license_number", label: "Driver license #" },
  { name: "preferred_vehicle", label: "Preferred vehicle" },
  { name: "tags", label: "Tags", type: "tags" },
  { name: "notes", label: "Notes", type: "textarea", span: 2 },
];

const COLUMNS = [
  { key: "name", label: "Name", render: (c) => (
    <Link to={`/customers/${c.id}`} className="hover:text-primary transition-colors" data-testid={`customer-link-${c.id}`}>
      <div className="font-heading inline-flex items-center gap-1">{c.first_name} {c.last_name} <ArrowRight size={10} className="opacity-50" /></div>
      {c.company && <div className="text-xs text-muted-foreground">{c.company}</div>}
    </Link>
  )},
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "age", label: "Age" },
  { key: "vip_tier", label: "Tier", render: (c) => <span className={`label-mono ${c.vip_tier === "platinum" || c.vip_tier === "gold" ? "text-primary" : ""}`}>{c.vip_tier || "standard"}</span> },
  { key: "nationality", label: "Nationality" },
];

const TIER_LABEL = { standard: "Standard", silver: "Silver", gold: "Gold", platinum: "Platinum VIP" };

const FILTERS = [
  { key: "all", label: "All" },
  { key: "vip", label: "VIP", test: (c) => c.vip_tier === "gold" || c.vip_tier === "platinum" },
  { key: "silver", label: "Silver", test: (c) => c.vip_tier === "silver" },
  { key: "standard", label: "Standard", test: (c) => !c.vip_tier || c.vip_tier === "standard" },
  { key: "company", label: "Corporate", test: (c) => Boolean(c.company) },
];

const renderCard = (c) => {
  const vip = c.vip_tier === "gold" || c.vip_tier === "platinum";
  const initials = `${(c.first_name || "?")[0] || ""}${(c.last_name || "")[0] || ""}`.toUpperCase();
  return (
    <Link to={`/customers/${c.id}`} className="block h-full" data-testid={`customer-link-${c.id}`}>
      <div className="border border-border bg-card p-4 h-full flex flex-col hover:border-primary/30 transition-colors duration-200">
        <div className="flex items-center gap-3">
          <span className={`w-12 h-12 rounded-xl flex items-center justify-center font-heading text-base shrink-0 border ${vip ? "bg-primary/12 border-primary/25 text-primary" : "bg-white/5 border-white/10 text-muted-foreground"}`}>
            {initials || <User size={17} />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-heading text-base truncate">{c.first_name} {c.last_name}</div>
            <div className="text-xs text-muted-foreground truncate mt-0.5">{c.company || c.email || c.phone || "—"}</div>
          </div>
          <ArrowRight size={13} className="text-muted-foreground shrink-0" />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-3">
          {vip && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/12 border border-primary/25 text-primary text-[10px] uppercase tracking-wider">
              <Crown size={10} weight="fill" /> {TIER_LABEL[c.vip_tier]}
            </span>
          )}
          {(c.tags || []).slice(0, 2).map((t) => (
            <span key={t} className="px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-[10px] text-muted-foreground">{t}</span>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-border/60 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="label-mono">Phone</div>
            <div className="text-sm mt-1 truncate">{c.phone || "—"}</div>
          </div>
          {c.age && <div className="text-right"><div className="label-mono">Age</div><div className="font-heading text-lg tnum mt-0.5">{c.age}</div></div>}
        </div>
      </div>
    </Link>
  );
};

export default function Customers() {
  return (
    <CrudList
      endpoint="/customers"
      fields={FIELDS}
      columns={COLUMNS}
      testid="customers"
      filters={FILTERS}
      renderCard={renderCard}
      emptyText="No clients yet. Click 'New' or import from a CSV."
    />
  );
}
